/**
 * View component for /module-certificates/:moduleCertificateId
 *
 * Displays a single moduleCertificate from the 'byId' map in the moduleCertificate reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import third party libraries
import ReactTooltip from 'react-tooltip';

import routeUtils from "../../../global/utils/routeUtils";

// import actions
import * as certificateTemplateActions from '../../certificateTemplate/certificateTemplateActions';
import * as moduleCertificateActions from '../moduleCertificateActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyUserActions from '../../studyUser/studyUserActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';
import * as userActions from '../../user/userActions';
import * as userEventActions from '../../userEvent/userEventActions';

// import global components
import Base from '../../../global/components/BaseComponent.js.jsx';
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Modal from '../../../global/components/modals/Modal.js.jsx';
// import ZendeskWidgetSettings from '../../../global/plugins/zendesk/ZendeskWidgetSettings.js.jsx';

// import moduleCertificate components
import AchievedModuleCertificate from '../components/AchievedModuleCertificate.js.jsx';
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import ModuleCertificateLayout from '../components/ModuleCertificateLayout.js.jsx';

// import moduleCertificate css modules
import moduleCertificateStyles from '../moduleCertificateModuleStyles.css';

class AdminSingleCertificateView extends Base {
  constructor(props) {
    super(props);
    this.state = {
      hideTooltip: false
      , attestationModalOpen: false
      , attestationHex: null
    }
    this._bind(
      '_checkPermissions'
      , '_handlePrint'
      , '_openAttestationModal'
      , '_closeAttestationModal'
      , '_handleSubmitAttestation'
      , '_handleResendAttestation'
    )
  }
  /*
   * NOTE: This component should be viewable by admin.viewAnalytics
   * but currently the user fetches are super-admin protected. We should be
   * able to grab users by study. ex: dispatch(userActions.fetchListIfNeeded('_group', match.params.studyId))
   * Then we can get the user from the map ex: selectedUser = userMap[studyUser._user] || null;
   * 
   * ^^ Is this okay? studyUsers with permissions already have access to all user info in their studies anyway.
   */
  componentDidMount() {
    const { dispatch, loggedInUser, match, location } = this.props;

    // old page - redirect to london certs view just in case
    // window.location.replace(`${window.agendaUrl}/certificates/${match.params.moduleCertificateId}`);

    // 1.14 send user access tracking
    dispatch(userEventActions.sendCreateUserEvent({
      eventType: "user-access"
      , eventAction: "module-certificate-record"
      , _contentModule: match.params.moduleCertificateId
    }));

    if(location.search) {
      let searchObj = routeUtils.objectFromQueryString(location.search)
      // console.log("searchObj", searchObj)
      if(searchObj.attestation) {
        this.setState({
          attestationModalOpen: true
          , attestationHex: searchObj.attestation
        })
      }
    }

    // We need to fetch the logged in study user to check permissions.
    if(match.path !== '/my-certificates/:moduleCertificateId') {
      /**
       * If we aren't at the above path, the user is trying to view another user's cert.
       * We need to fetch the logged in study user to check permissions.
       */
      dispatch(studyUserActions.fetchListIfNeeded('_user', loggedInUser._id, '_group', match.params.studyId));
      dispatch(userActions.invalidateSelected());
    }
    // TODO: these fetches, or maybe the component itself, should be separated out based on different use cases
    // can be admin list, study admin list, or regular user list
    if(match.params.userId) {
      dispatch(userActions.fetchSingleIfNeeded(match.params.userId));
    }
    // if(match.params.studyUserId) {
    //   dispatch(studyUserActions.fetchSingleIfNeeded(match.params.studyUserId)).then(suRes => {
    //     if(suRes.success) {
    //       dispatch(userActions.fetchSingleIfNeeded(suRes.item._user));
    //     }
    //   })
    // }
    if(match.params.studyId) {
      dispatch(userActions.fetchListIfNeeded('_group', match.params.studyId))
    }
    if(match.params.studyUserId) {
      dispatch(studyUserActions.fetchSingleIfNeeded(match.params.studyUserId))
    }
    dispatch(moduleCertificateActions.fetchSingleIfNeeded(match.params.moduleCertificateId)).then((certAction) => {
      if(certAction.success) {
        if(certAction.item._user == "" + loggedInUser._id) {
          // fetch logged in user
          dispatch(userActions.fetchSingleIfNeeded('get-logged-in'))
        } else {
          // fetch by id, admin only. Possibly redundant since we fetch all users by study above and use the userMap to grab the user object.
          dispatch(userActions.fetchSingleIfNeeded(certAction.item._user));
        }
        dispatch(contentModuleActions.fetchSingleIfNeeded(certAction.item._contentModule)).then((tmAction) => {
          if(tmAction.success && tmAction.item._organization) {
            dispatch(sponsorActions.fetchSingleIfNeeded(tmAction.item._organization));
          }
          if(tmAction.success && tmAction.item._certificateTemplate) {
            dispatch(certificateTemplateActions.fetchSingleIfNeeded(tmAction.item._certificateTemplate));
          }
        })
      }
    });
  }

  _checkPermissions(certificate, loggedInStudyUser) {
    const { loggedInUser, match } = this.props;
    if(match.path === '/my-certificates/:moduleCertificateId') {
      // if we hit this route then a user is viewing his own certificate.
      // the user shouldn't be able to view other user's certificates.
      return loggedInUser._id === certificate._user
      // Any other route and the user has to be a super-admin or studyUser.admin or studyUser.permissions.include('view-reports')
    } else if(
      loggedInUser.admin
      || loggedInStudyUser
      && loggedInStudyUser.admin
      || loggedInStudyUser
      && loggedInStudyUser.permissions.includes('view-reports') // double check these too 
      ) {
        return true
      } else {
        // User is not allowed to view this page.
        return false
      }
  }

  _handlePrint() {
    /**
     * Make sure the tooltip is hidden before calling window.print()
     * otherwise it shows up on the PDF. Annoying.
     */
    this.setState({
      hideTooltip: true
    }, () => {
      window.print()
      this.setState({
        hideTooltip: false
      });
    });
  }

  _openAttestationModal() {
    this.setState({attestationModalOpen: true})
  }

  _closeAttestationModal() {
    this.setState({attestationModalOpen: false})
  }

  _handleSubmitAttestation() {
    // console.log(this.props.location.search)
    // console.log("SUBMITTING")
    this.props.dispatch(moduleCertificateActions.sendModuleCertificateAttestation(this.props.match.params.moduleCertificateId, this.state.attestationHex))
    .then(result => {
      // console.log("result", result)
      if(!result.success) {
        alert(`${result.message} - Please refresh the page and try again.`)
      } else {
        // reload the page without the search params
        window.location.href = this.props.location.pathname; // refresh without search param
      }
    })
  }

  _handleResendAttestation() {
    // console.log("RESENDING")
    this.props.dispatch(moduleCertificateActions.sendModuleCertificateAttestationEmail(this.props.match.params.moduleCertificateId))
    .then(result => {
      // console.log("result", result)
      if(!result.success) {
        alert(`${result.message} - Please refresh the page and try again.`)
      } else {
        // refresh the page I guess?
        window.location.reload();
      }
    })
  }

  render() {
    const {
      certificateTemplateStore
      , location
      , loggedInUser
      , match
      , moduleCertificateStore
      , sponsorStore
      , studyUserStore
      , contentModuleStore
      , userStore
    } = this.props;

    /**
     * use the selected.getItem() utility to pull the actual moduleCertificate object from the map
     */
    const selectedModuleCertificate = moduleCertificateStore.selected.getItem();
    const selectedContentModule = contentModuleStore.selected.getItem();
    // const selectedStudyUser = studyUserStore.selected.getItem();
    // const selectedUser = userStore.selected.getItem();

    // Get logged in study user to check permissions.
    const loggedInStudyUserList = loggedInUser && loggedInUser._id && match.params.studyId ? studyUserStore.util.getList('_user', loggedInUser._id, '_group', match.params.studyId) : null;
    // it's weird to just grab the first item in a 'list' like this, but this list should only ever have ONE item. If it has more then something is wrong.
    const loggedInStudyUser = loggedInStudyUserList && loggedInStudyUserList.length === 1 ? loggedInStudyUserList[0] : null;

    const selectedUser = (
      selectedModuleCertificate ?
        userStore.byId[selectedModuleCertificate._user]
      :
        null
    );

    const isEmpty = (
      !selectedModuleCertificate
      || !selectedModuleCertificate._id
      || moduleCertificateStore.selected.didInvalidate
      || !selectedContentModule
      || !selectedContentModule._id
      || contentModuleStore.selected.didInvalidate
      || !selectedUser
    );

    const isFetching = (
      moduleCertificateStore.selected.isFetching
      || contentModuleStore.selected.isFetching
      || userStore.selected.isFetching
    );

    const permissionToView = (
      !isEmpty && !isFetching ?
        this._checkPermissions(selectedModuleCertificate, loggedInStudyUser) : null
    );

    const selectedSponsor = (
      !isEmpty
      && selectedContentModule
      && selectedContentModule._organization ?
        sponsorStore.selected.getItem() : null
    );

    const selectedCertTemplate = (
      !isEmpty
      && selectedContentModule
      && selectedContentModule._certificateTemplate ?
        certificateTemplateStore.selected.getItem() : null
    );

    return (
      <AuthoringLayout>
        <Helmet
          title={"Certificate of Completion" + (!isEmpty ? ` for ${selectedContentModule.title}` : '') + " | Pro-ficiency"}
        />
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              {/* <Breadcrumbs links={location.state.breadcrumbs} /> */}
              {isEmpty ?
                (isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                permissionToView ? 
                  <div style={{ opacity: isFetching ? 0.5 : 1 }}>
                    { selectedModuleCertificate.status == "pending-attestation" ? 
                      <div className="pending-wrapper">
                        { this.state.attestationHex ?
                          <p>I attest that I am who I say I am and that I did this training.</p>
                          :
                          <p>You should have received an email containing a link to complete the attestation process. If you wish to have the email resent, please press the button below.</p>
                        }
                        { this.state.attestationHex ? 
                          <button
                            className="yt-btn pending"
                            onClick={this._handleSubmitAttestation}
                          >
                            Submit Attestation
                          </button>
                          :  
                          <button
                            className="yt-btn pending"
                            onClick={this._handleResendAttestation}
                          >
                            Resend Email
                          </button>
                        }
                      </div>
                      : null
                    }
                    <div className="yt-row right u-hidePrint">
                      <button
                        className="yt-btn x-small primary"
                        data-tip 
                        data-for={`downloadTip_${selectedModuleCertificate._id}`}
                        onClick={this._handlePrint}
                        style={{marginBottom: 16, marginRight: 4}}
                      >
                        Print
                        <sup style={{marginLeft: '.1rem'}}><i className='far fa-info-circle'/></sup>
                      </button>
                      { loggedInUser && loggedInUser.admin ? 
                        <a
                          className="yt-btn x-small secondary"
                          href={`/api/module-certificates/${selectedModuleCertificate._id}/pdf`}
                          style={{marginBottom: 16, marginRight: 4}}
                          _target='blank'
                        >
                          ADMIN - Download As PDF
                        </a>
                        : null
                      }
                      { loggedInUser && loggedInUser.admin ? 
                        <Link
                          className="yt-btn x-small tertiary"
                          to={`/admin/audit-logs/module-certificate/${selectedModuleCertificate._id}`}
                          style={{marginBottom: 16}}
                          target="_blank"
                        >
                          Object History &rarr;
                        </Link>
                        : null
                      }
                      
                    </div>
                    <div className="cert-wrapper">
                      <AchievedModuleCertificate
                        certificateTemplate={selectedContentModule._certificateTemplate ? certificateTemplateStore.byId[selectedContentModule._certificateTemplate] : null }
                        moduleCertificate={selectedModuleCertificate}
                        sponsor={selectedSponsor}
                        contentModule={selectedContentModule}
                        user={selectedUser}
                      />
                    </div>
                    { !this.state.hideTooltip ? 
                      <ReactTooltip
                        aria-haspopup='true'
                        className='custom-tooltip -bright'
                        id={`downloadTip_${selectedModuleCertificate._id}`}
                        place="left"
                      >
                        <div style={{textAlign: 'right'}}>
                          <p className='-tip-title'>
                            <strong>YOU CAN PRINT HERE!</strong>
                            <i className='far fa-print'/>
                          </p>
                          <p>
                            Depending on your operating system, you may be able to print to an electronic <br/>
                            format like PDF. You can also take a screenshot of the certificate page. On a <br/>
                            <strong>Mac/Apple</strong> use <strong>Shift+Command+3 </strong>
                            and on <strong>Windows</strong> use <strong>Alt+PrtScn</strong>.
                          </p>
                        </div>
                      </ReactTooltip>
                      :
                      null
                    }
                  </div>
                :
                  <div>
                    <h2>Unauthorized</h2>
                    <p>You do not have permission to access this resource.</p>
                    <Link to={match.params.studyId ? '/studies-dashboard' : '/my-certificates'}>Go home</Link>
                  </div>
              }
             
            </div>
          </section>
        </div>
        {/* <ZendeskWidgetSettings hideLauncher={true} /> */}
      </AuthoringLayout>
    )
  }
}

AdminSingleCertificateView.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    certificateTemplateStore: store.certificateTemplate
    , loggedInUser: store.user.loggedIn.user
    , moduleCertificateStore: store.moduleCertificate
    , sponsorStore: store.sponsor
    , studyUserStore: store.studyUser
    , contentModuleStore: store.contentModule
    , userStore: store.user
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleCertificateView)
);



// WEBPACK FOOTER //
// modules/moduleCertificate/views/AdminSingleCertificateView.jsx